// src/App.js
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useIsMobile } from './hooks/useIsMobile';

// --- MOBILE COMPONENTS ---
import MobileHeader from './components/MobileHeader';
import MobileNav from './components/MobileNav';
import './Mobile.css';

// --- CONTEXT & PROTECTED ROUTE ---
import { AuthContext } from './context/AuthContext';
import { useModal } from './context/ModalContext';
import ProtectedRoute from './components/ProtectedRoute';

// --- LAYOUT COMPONENTS ---
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import GlobalModal from './components/GlobalModal';
import GlobalUploadProgress from './components/GlobalUploadProgress';

// --- PAGES ---
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import CommunityDetails from './pages/CommunityDetails';
import Tickets from './pages/Tickets';
import TicketDetails from './pages/TicketDetails';
import Technicians from './pages/Technicians';
import TechnicianDetails from './pages/TechnicianDetails';
import Inventory from './pages/Inventory';
import MasterInventory from './pages/MasterInventory';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Schedule from './pages/Schedule';
import CreateAppointment from './pages/CreateAppointment';
import UserManagement from './pages/UserManagement';
import QuickItemCheckout from './pages/QuickItemCheckout';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// --- Helper for Web Push ---
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const AppContent = () => {
    const navigate = useNavigate();

    // --- GLOBAL STATE & AUTHENTICATION ---
    const { user, logout, settings, updateSettings, isSettingsLoading } = useContext(AuthContext);
    const { showAlert } = useModal();
    const isAuthenticated = !!user;
    const [isLoading, setIsLoading] = useState(true);
    const isMobile = useIsMobile();
    
    // --- WEB PUSH SUBSCRIPTION ---
    useEffect(() => {
        if (isAuthenticated && 'serviceWorker' in navigator && 'PushManager' in window) {
            const publicVapidKey = "BIsltuAnCk3rcJw9OVcIblNtSfudPJ_f3G0N6o4TXtqlljlXKffj10bW1SVm87rM82PRSvqD3DgQ5im2LL4K0Kk"; 

            const subscribeUser = async () => {
                try {
                    const registration = await navigator.serviceWorker.ready;
                    let subscription = await registration.pushManager.getSubscription();
                    
                    if (!subscription && publicVapidKey && publicVapidKey !== "YOUR_PUBLIC_VAPID_KEY_HERE") {
                        subscription = await registration.pushManager.subscribe({
                            userVisibleOnly: true,
                            applicationServerKey: urlBase64ToUint8Array(publicVapidKey)
                        });
                        console.log('✅ [Push] Subscribed to push notifications!');
                    }

                    if (subscription) {
                        await fetch(`${API_BASE_URL}/users/push-subscribe`, {
                            method: 'POST',
                            body: JSON.stringify(subscription),
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            }
                        });
                    }
                } catch (error) {
                    console.error('[Push] Failed to subscribe user: ', error);
                }
            };

            subscribeUser();
        }
    }, [isAuthenticated]);

    // ---------------------------------------------------------
    // Global Hotkey Listener
    // ---------------------------------------------------------
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.shiftKey && event.altKey && (event.key === 'T' || event.key === 't')) {
                event.preventDefault(); 
                const canManage = user?.roles?.some(role => ['Giga Admin', 'Admin'].includes(role));
                
                if (canManage) {
                    navigate('/tickets', { state: { action: 'new' } });
                }
            }

            if (event.shiftKey && event.altKey && (event.key === 'U' || event.key === 'u')) {
                event.preventDefault();
                const canManageUsers = user?.roles?.some(role => ['Giga Admin', 'Admin'].includes(role));
                
                if (canManageUsers) {
                    navigate('/settings/users', { state: { action: 'newUser' } });
                }
            }

            if (event.shiftKey && event.altKey && (event.key === 'C' || event.key === 'c')) {
                event.preventDefault();
                const canManageCommunities = user?.roles?.some(role => ['Giga Admin', 'Admin'].includes(role));
                
                if (canManageCommunities) {
                    navigate('/customers', { state: { action: 'newCommunity' } });
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [user, navigate]);

    // ---------------------------------------------------------
    // Global Modal Escape Manager (Fixes Nested Modal Bug)
    // ---------------------------------------------------------
    useEffect(() => {
        const handleGlobalEscape = (e) => {
            if (e.key === 'Escape') {
                // 1. If user is typing in an input field, blur it first instead of closing the modal immediately
                if (document.activeElement && ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement.tagName)) {
                    document.activeElement.blur();
                    return; 
                }

                // 2. Find all visible modal overlays
                const overlays = Array.from(document.querySelectorAll('.modal-overlay'))
                    .filter(el => {
                        const style = window.getComputedStyle(el);
                        return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
                    });

                if (overlays.length > 0) {
                    e.preventDefault();
                    e.stopImmediatePropagation(); // Kill all rogue component-level Escape listeners

                    // 3. Find the top-most modal
                    let topOverlay = overlays[0];
                    let maxZ = parseInt(window.getComputedStyle(topOverlay).zIndex) || 0;

                    for (let i = 1; i < overlays.length; i++) {
                        const currentZ = parseInt(window.getComputedStyle(overlays[i]).zIndex) || 0;
                        // Use >= to favor elements naturally stacked higher in the DOM tree
                        if (currentZ >= maxZ) {
                            maxZ = currentZ;
                            topOverlay = overlays[i];
                        }
                    }

                    // 4. Click the close button of ONLY the top-most modal
                    const closeBtn = topOverlay.querySelector('.modal-close-btn');
                    if (closeBtn) {
                        closeBtn.click();
                    } else {
                        // Fallback if the modal lacks a standard close button
                        topOverlay.click();
                    }
                }
            }
        };

        // Attach to the capture phase so it runs BEFORE individual components catch the keypress
        window.addEventListener('keydown', handleGlobalEscape, true);
        return () => window.removeEventListener('keydown', handleGlobalEscape, true);
    }, []);
    // ---------------------------------------------------------

    // --- DATA STATES ---
    const [communities, setCommunities] = useState([]);
    const [technicians, setTechnicians] = useState([]);
    const [inventory, setInventory] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [sites, setSites] = useState([]);
    const [specializations, setSpecializations] = useState([]);
    const [credentials, setCredentials] = useState([]);
    const [masterInventoryItems, setMasterInventoryItems] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [ticketGroups, setTicketGroups] = useState([]);
    const [users, setUsers] = useState([]);

    // --- State to track online users ---
    const [onlineUserIds, setOnlineUserIds] = useState([]); 

    // --- STATE FOR BACKGROUND UPLOADS ---
    const [backgroundUploads, setBackgroundUploads] = useState({});

    // --- UI/UX STATES ---
    const isDarkMode = settings.theme === 'dark';
    useEffect(() => {
        document.body.classList.toggle('dark-mode', isDarkMode);
    }, [isDarkMode]);
    const toggleDarkMode = () => updateSettings({ theme: isDarkMode ? 'light' : 'dark' });

    const animationsEnabled = settings.animationsEnabled;
    useEffect(() => {
        document.body.classList.toggle('animations-disabled', !animationsEnabled);
    }, [animationsEnabled]);
    const toggleAnimations = () => updateSettings({ animationsEnabled: !animationsEnabled });

    useEffect(() => {
        document.body.classList.toggle('is-mobile', isMobile);
    }, [isMobile]);
    
    const hasFetchedData = useRef(false);

    // --- NOTIFICATION MANAGEMENT ---
    const markNotificationsAsRead = useCallback(() => {
        setTimeout(() => {
            setNotifications(prev => 
                prev.map(n => n.read ? n : { ...n, read: true })
            );
        }, 2000);
   }, []);
   const clearAllNotifications = useCallback(() => {
        setNotifications([]);
    }, []);

    // --- DATA FETCHING & API CALLS ---
    const fetchData = useCallback(async (endpoint, setter) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/${endpoint}`, { headers: { 'Authorization': `Bearer ${token || ''}` } });
            
            if (response.status === 401) { logout(); throw new Error('Unauthorized'); }
            if (response.status === 403) { return; }
            if (!response.ok) throw new Error(`Failed to fetch ${endpoint}`);

            const data = await response.json();
            setter(data.data || data);
        } catch (error) { 
            console.error(`Error fetching ${endpoint}:`, error); 
            throw error; 
        }
    }, [logout]);
    
    const refreshData = useCallback(async () => {
        setIsLoading(true);
        try {
            const corePromises = [
                fetchData('communities', setCommunities), 
                fetchData('technicians', setTechnicians), 
                fetchData('inventory', setInventory), 
                fetchData('contacts', setContacts), 
                fetchData('sites', setSites), 
                fetchData('specializations', setSpecializations), 
                fetchData('credentials', setCredentials), 
                fetchData('master-inventory', setMasterInventoryItems), 
                fetchData('appointments', setAppointments), 
                fetchData('tickets', setTickets), 
                fetchData('documents', setDocuments), 
                fetchData('ticket-groups', setTicketGroups),
                fetchData('departments', setDepartments)
            ];

            if (user && (user.roles.includes('Giga Admin') || user.roles.includes('Admin'))) {
                corePromises.push(fetchData('users', setUsers));
            }

            await Promise.all(corePromises);

        } catch (error) { 
            console.error("Data refresh error:", error);
        } finally { 
            setIsLoading(false);
        }
    }, [fetchData, user]);

    const apiCall = useCallback(async (endpoint, method = 'GET', body = null) => {
        try {
            const token = localStorage.getItem('token');
            const options = { method, headers: { 'Authorization': `Bearer ${token || ''}` } };
            if (body) {
                options.headers['Content-Type'] = 'application/json';
                options.body = JSON.stringify(body);
            }
 
            const response = await fetch(`${API_BASE_URL}/${endpoint}`, options);
            if (response.status === 401) { logout(); return { success: false, error: 'Session expired. Please log in again.' }; }
            
            if (!response.ok) { const errorData = await response.json(); throw new Error(errorData.message || `Failed to ${method} ${endpoint}`); }
      
            if (response.status === 204) return { success: true };
            const result = await response.json();
        
            return { success: true, data: result };
        } catch (error) {
            console.error(`API call error on ${endpoint}:`, error);
            showAlert(`Error: ${error.message}`, 'API Error');
            return { success: false, error: error.message };
        }
    }, [logout, showAlert]);
    
    const apiCallWithFile = useCallback((endpoint, formData, onProgress) => {
        return new Promise((resolve) => {
            const token = localStorage.getItem('token');
            const xhr = new XMLHttpRequest();
            
            xhr.open('POST', `${API_BASE_URL}/${endpoint}`);
            xhr.setRequestHeader('Authorization', `Bearer ${token || ''}`);

            if (xhr.upload && onProgress) {
                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable) {
                        const percentComplete = Math.round((event.loaded / event.total) * 100);
                        onProgress(percentComplete);
                    }
                };
            }

            xhr.onload = () => {
                if (xhr.status === 401) {
                    logout();
                    resolve({ success: false, error: 'Session expired. Please log in again.' });
                } else if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        const result = JSON.parse(xhr.responseText);
                        resolve({ success: true, data: result });
                    } catch (e) {
                        resolve({ success: true, data: null });
                    }
                } else {
                    let errorMessage = `Failed to upload file to ${endpoint}`;
                    try {
                        const errorData = JSON.parse(xhr.responseText);
                        errorMessage = errorData.message || errorMessage;
                    } catch (e) {}
                    resolve({ success: false, error: errorMessage });
                }
            };

            xhr.onerror = () => {
                console.error(`API file call error on ${endpoint}`);
                resolve({ success: false, error: 'Network error' });
            };

            xhr.send(formData);
        });
    }, [logout]);

    const apiCallForBlob = useCallback(async (endpoint) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/${endpoint}`, { headers: { 'Authorization': `Bearer ${token || ''}` } });
            if (response.status === 401) { logout(); return null; }
            if (!response.ok) { return null; }
            const blob = await response.blob();
            return blob;
        } catch (error) {
            console.error(`Blob fetch error on ${endpoint}:`, error);
            return null;
        }
    }, [logout]);

    useEffect(() => { 
        if (isAuthenticated && !hasFetchedData.current) { 
            refreshData(); 
            hasFetchedData.current = true;
        } else if (!isAuthenticated) { 
            hasFetchedData.current = false; 
            setIsLoading(false); 
        } 
    }, [isAuthenticated, refreshData]);

    // --- HELPER: Merge new ticket data while preserving local upload progress ---
    const mergeTicketUpdates = useCallback((prevTickets, updatedTicketsArray) => {
        const ticketsMap = new Map(prevTickets.map(t => [t._id, t]));
    
        updatedTicketsArray.forEach(updatedTicket => {
            const existingTicket = ticketsMap.get(updatedTicket._id);
            if (existingTicket) {
                const mergedNotes = updatedTicket.notes.map(newNote => {
                    const existingNote = existingTicket.notes.find(n => n._id === newNote._id);
                    if (existingNote && existingNote.uploadProgress !== undefined) {
                        return { ...newNote, uploadProgress: existingNote.uploadProgress, isUploading: true };
                    }
                    return newNote;
                });
                ticketsMap.set(updatedTicket._id, { ...updatedTicket, notes: mergedNotes });
            } else {
                ticketsMap.set(updatedTicket._id, updatedTicket);
            }
        });
        
        return Array.from(ticketsMap.values());
    }, []);

    // Helper to check ticket visibility permissions
    const isTicketVisibleToUser = useCallback((ticket) => {
        if (!user) return false;
        
        // Admins and Giga Admins see everything
        if (user.roles.includes('Giga Admin') || user.roles.includes('Admin')) return true;
        
        // Clients only see their own tickets
        if (user.roles.includes('Client')) {
            return ticket.client === user.linkedCommunityName || true; 
        }

        // If ticket has NO departments assigned, everyone sees it (Unassigned)
        if (!ticket.departments || ticket.departments.length === 0) return true;

        // Check Department Overlap
        const userDepts = user.departments || [];
        const ticketDepts = ticket.departments || [];
        
        return ticketDepts.some(dept => userDepts.includes(dept));
    }, [user]);

    // --- WEBSOCKET CONNECTION ---
    useEffect(() => {
        if (!isAuthenticated) return;

        let socket = null;
        let reconnectTimer = null;
        let isComponentMounted = true;

        const connect = () => {
            if (!isComponentMounted) return;

            const token = localStorage.getItem('token');
            const apiUrl = new URL(process.env.REACT_APP_API_URL || window.location.origin);
            const protocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
            const wsUrl = `${protocol}//${apiUrl.host}/ws?token=${token}`;
            
            socket = new WebSocket(wsUrl);

            socket.onopen = () => {
                if (reconnectTimer) clearTimeout(reconnectTimer);
            };

            socket.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);

                    switch (message.type) {
                        case 'ONLINE_USERS_UPDATED':
                            setOnlineUserIds(message.payload);
                            break;

                        case 'NEW_NOTIFICATION':
                            setNotifications(prev => [message.payload, ...prev]);
                            
                            try {
                                const audio = new Audio('/sounds/NewTicket.mp3'); 
                                audio.volume = 0.5;
                                const playPromise = audio.play();
                                if (playPromise !== undefined) {
                                    playPromise.catch(error => {
                                        console.warn("Audio play blocked:", error);
                                    });
                                }
                            } catch (e) {}

                            if ('serviceWorker' in navigator && 'Notification' in window && Notification.permission === 'granted') {
                                navigator.serviceWorker.ready.then(registration => {
                                    registration.showNotification("Ramco: New Ticket Assigned", {
                                        body: message.payload.message,
                                        icon: '/favicon.ico', 
                                        badge: '/favicon.ico', 
                                        tag: 'ticket-notification',
                                        silent: true, 
                                        data: { url: `/tickets/${message.payload.ticketId}` }
                                    });
                                });
                            }
                            break;
                        case 'MASTER_INVENTORY_UPDATED': 
                            setMasterInventoryItems(message.payload); 
                            break;
                        case 'INVENTORY_UPDATED': 
                            setInventory(message.payload); 
                            break;
                        case 'USERS_UPDATED': 
                            setUsers(message.payload); 
                            break;
                        
                        case 'TICKETS_UPDATED':
                        case 'TICKETS_BATCH_UPDATED': {
                            const updatedTicketsArray = Array.isArray(message.payload) ? message.payload : [message.payload];
                            const visibleTickets = [];
                            const invisibleTicketIds = [];

                            updatedTicketsArray.forEach(t => {
                                if (isTicketVisibleToUser(t)) {
                                    visibleTickets.push(t);
                                } else {
                                    invisibleTicketIds.push(t._id);
                                }
                            });

                            setTickets(prev => {
                                let newState = prev.filter(t => !invisibleTicketIds.includes(t._id));
                                return mergeTicketUpdates(newState, visibleTickets);
                            });
                            break;
                        }

                        case 'TICKET_UPDATED': {
                            const updatedTicket = message.payload;
                            if (isTicketVisibleToUser(updatedTicket)) {
                                setTickets(prev => mergeTicketUpdates(prev, [updatedTicket]));
                            } else {
                                setTickets(prev => prev.filter(t => t._id !== updatedTicket._id));
                            }
                            break;
                        }
                        
                        case 'TICKET_DELETED': {
                            const { _id } = message.payload;
                            setTickets(prev => prev.filter(t => t._id !== _id));
                            break;
                        }
    
                        case 'TICKETS_DELETED_BATCH': {
                            const { ids } = message.payload;
                            setTickets(prev => prev.filter(t => !ids.includes(t._id)));
                            break;
                        }
                        
                        case 'COMMUNITIES_UPDATED': setCommunities(message.payload); break;
                        case 'TECHNICIANS_UPDATED': setTechnicians(message.payload); break;
                        case 'SITES_UPDATED': setSites(message.payload); break;
                        case 'DEPARTMENTS_UPDATED': setDepartments(message.payload); break;
                        case 'CONTACTS_UPDATED': setContacts(message.payload); break;
                        case 'TICKET_GROUPS_UPDATED': setTicketGroups(message.payload); break;
                        case 'DOCUMENTS_UPDATED': setDocuments(message.payload); break;
                        case 'SPECIALIZATIONS_UPDATED': setSpecializations(message.payload); break;
                        case 'CREDENTIALS_UPDATED': setCredentials(message.payload); break;
                        case 'APPOINTMENT_UPDATED': {
                            const updatedAppointment = message.payload;
                            setAppointments(prev => {
                                const index = prev.findIndex(a => a._id === updatedAppointment._id);
                                if (index > -1) {
                                    const newAppointments = [...prev];
                                    newAppointments[index] = updatedAppointment;
                                    return newAppointments;
                                }
                                return [updatedAppointment, ...prev];
                            });
                            break;
                        }
                        case 'APPOINTMENT_DELETED': {
                            const { _id } = message.payload;
                            setAppointments(prev => prev.filter(a => a._id !== _id));
                            break;
                        }
                    
                        default:
                            console.warn(`Unknown WebSocket message type: ${message.type}`);
                            break;
                    }
                } catch (error) {
                    console.error('Error processing WebSocket message:', error);
                }
            };

            socket.onclose = (event) => {
                socket = null;
                if (isComponentMounted && localStorage.getItem('token')) {
                    reconnectTimer = setTimeout(connect, 3000);
                }
            };

            socket.onerror = (error) => {
                console.error('❌ [App] WS Error:', error); 
                if (socket) {
                    socket.close();
                }
            };
        };
        connect();

        return () => {
            isComponentMounted = false;
            if (reconnectTimer) clearTimeout(reconnectTimer);
            if (socket) {
                socket.onclose = null;
                socket.close();
            }
        };
    }, [isAuthenticated, mergeTicketUpdates, isTicketVisibleToUser]);

    // ... [Wrappers for Create, Update, Delete] ...
    const createWrapper = (endpoint, setter, addToArray = true) => async (data) => {
        const result = await apiCall(endpoint, 'POST', data);
        if (result.success) {
            if (addToArray) { 
                setter(prev => {
                    const exists = prev.some(item => item._id === result.data._id);
                    if (exists) {
                        return prev.map(item => item._id === result.data._id ? result.data : item);
                    }
                    return [result.data, ...prev];
                }); 
            } 
            else { setter(result.data); }
        }
        return result;
    };
    const updateWrapper = (endpoint, setter) => async (id, data) => {
        const result = await apiCall(`${endpoint}/${id}`, 'PUT', data);
        if (result.success) {
            const updates = Array.isArray(result.data) ? result.data : [result.data];
            setter(prev => {
                const newItems = [...prev];
                let isUpdated = false;
                updates.forEach(updatedItem => {
                    const index = newItems.findIndex(item => item._id === updatedItem._id);
                    if (index > -1) { newItems[index] = updatedItem; isUpdated = true; }
                });
                return isUpdated ? newItems : prev;
            });
        }
        return result;
    };
    
    const deleteWrapper = (endpoint, setter) => async (id) => {
        const result = await apiCall(`${endpoint}/${id}`, 'DELETE');
        if (result.success) {
            if (Array.isArray(result.data)) { setter(result.data); } 
            else { setter(prev => prev.filter(item => item._id !== id)); }
        }
        return result;
    };

    const deleteMultipleWrapper = (endpoint, setter) => async (ids) => {
        const result = await apiCall(`${endpoint}/delete-multiple`, 'POST', { ids });
        if (result.success) {
            if (Array.isArray(result.data)) { setter(result.data); } 
            else { setter(prev => prev.filter(item => !ids.includes(item._id))); }
        }
        return result;
    };

    const handleCreateTicket = createWrapper('tickets', setTickets, true);
    const handleUpdateTicket = updateWrapper('tickets', setTickets);
    const handleDeleteTicket = deleteWrapper('tickets', setTickets);
    const handleDeleteMultipleTickets = deleteMultipleWrapper('tickets', setTickets);
    
    const handleCreateSubTicket = async (parentId, subTicketData) => {
        const result = await apiCall(`tickets/${parentId}/subticket`, 'POST', subTicketData);
        if (result.success) {
            const updatedTicketsArray = result.data;
            setTickets(prev => {
                const ticketsMap = new Map(prev.map(t => [t._id, t]));
                updatedTicketsArray.forEach(updatedTicket => { 
                    ticketsMap.set(updatedTicket._id, updatedTicket); 
                });
                return Array.from(ticketsMap.values());
            });
        }
        return result;
    };

    const handleAddCommunity = createWrapper('communities', setCommunities, true);
    const handleUpdateCommunity = updateWrapper('communities', setCommunities);
    const handleDeleteCommunity = deleteWrapper('communities', setCommunities);
    const handleDeleteMultipleCommunities = deleteMultipleWrapper('communities', setCommunities);
    
    const handleAddTechnician = createWrapper('technicians', setTechnicians, false);
    const handleUpdateTechnician = updateWrapper('technicians', setTechnicians);
    const handleDeleteMultipleTechnicians = deleteMultipleWrapper('technicians', setTechnicians);
    
    // ✅ ADD ITEM LOGIC - Handles both FormData and JSON seamlessly
    const handleAddItem = async (data) => {
        const isFormData = data instanceof FormData;
        
        // Route dynamically to the correct endpoint
        const endpoint = 'inventory'; // ✅ FIXED: Route all to inventory, backend handles assemblies
        
        let result;
        if (isFormData) {
            result = await apiCallWithFile(endpoint, data);
        } else {
            result = await apiCall(endpoint, 'POST', data);
        }
        
        if (result.success) {
            if (result.data && Array.isArray(result.data.data)) {
                setInventory(result.data.data);
            } else if (Array.isArray(result.data)) {
                setInventory(result.data);
            }
        }
        return result;
    };

    const handleAdjustStock = updateWrapper('inventory', setInventory);
    const handleTransferInventory = async (data) => {
        const result = await apiCall('inventory/transfer', 'POST', data);
        if(result.success) await refreshData();
        return result;
    };

    const handleConsumeItem = async (data) => {
        const result = await apiCall('inventory/consume', 'POST', data);
        if (result.success) {
            await refreshData(); 
        } else {
            showAlert(result.error, "Consumption Failed");
        }
        return result;
    };

    const handleDeleteMultipleInventoryItems = deleteMultipleWrapper('inventory', setInventory);
    
    const handleAddContact = createWrapper('contacts', setContacts, false);
    const handleUpdateContact = updateWrapper('contacts', setContacts);
    const handleDeleteContact = deleteWrapper('contacts', setContacts);
    const handleAddCredential = createWrapper('credentials', setCredentials, false);
    const handleUpdateCredential = updateWrapper('credentials', setCredentials);
    const handleDeleteCredential = deleteWrapper('credentials', setCredentials);

    const handleAddSite = createWrapper('sites', setSites, false);
    const handleDeleteMultipleSites = deleteMultipleWrapper('sites', setSites);
    const handleAddSpecialization = createWrapper('specializations', setSpecializations, false);
    const handleUpdateSpecialization = updateWrapper('specializations', setSpecializations);
    const handleDeleteSpecialization = deleteWrapper('specializations', setSpecializations);
    const handleAddDepartment = createWrapper('departments', setDepartments, false);
    const handleUpdateDepartment = updateWrapper('departments', setDepartments);
    const handleDeleteDepartment = deleteWrapper('departments', setDepartments);
    
    const handleAddGroup = createWrapper('ticket-groups', setTicketGroups, true);
    const handleDeleteGroup = deleteWrapper('ticket-groups', setTicketGroups);

    const handleAddAppointment = createWrapper('appointments', setAppointments, true);
    const handleUpdateAppointment = updateWrapper('appointments', setAppointments);
    const handleDeleteAppointment = deleteWrapper('appointments', setAppointments);
    
    const handleAddDocument = async (formData) => {
        const result = await apiCallWithFile('documents/upload', formData);
        if (result.success) await refreshData();
        return result;
    };
    const handleDeleteDocument = async (id) => {
        const result = await apiCall(`documents/${id}`, 'DELETE');
        if (result.success) await refreshData();
        return result;
    };

    const handleAddMasterItemWithFile = async (formData) => {
        const result = await apiCallWithFile('master-inventory', formData);
        return result;
    };

    const handleUpdateMasterItemWithFile = async (id, formData) => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${API_BASE_URL}/master-inventory/${id}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token || ''}` },
                body: formData
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message || 'Update failed');

            setMasterInventoryItems(prev => prev.map(item => item._id === result._id ? result : item));
            return { success: true, data: result };
        } catch (error) {
            showAlert(`Error: ${error.message}`, 'API Error');
            return { success: false, error: error.message };
        }
    };
    
    const handleAddNote = useCallback(async (ticketId, noteData) => {
        const { text, files } = noteData;
        const tempNoteId = `temp-note-${Date.now()}`;
        const hasAttachments = files && files.length > 0;

        const optimisticAttachments = hasAttachments ? files.map(file => ({
            _id: `temp-file-${Math.random()}`,
            originalName: file.name,
            fileType: file.type,
            filePath: URL.createObjectURL(file), 
            isOptimistic: true
        })) : [];

        const optimisticNote = {
            _id: tempNoteId,
            text: text,
            author: user?.name || 'You',
            authorId: user?.id,
            createdAt: new Date().toISOString(),
            attachments: optimisticAttachments,
            isUploading: true, 
            uploadProgress: 0
        };

        setTickets(prev => prev.map(t => {
            if (t._id === ticketId) {
                return { ...t, notes: [optimisticNote, ...t.notes] };
            }
            return t;
        }));

        const formData = new FormData();
        formData.append('text', text);
        if (hasAttachments) {
            files.forEach(file => formData.append('attachments', file));
        }

        const result = await apiCallWithFile(`tickets/${ticketId}/notes`, formData, (percent) => {
            setTickets(prev => prev.map(t => {
                if (t._id === ticketId) {
                    return {
                        ...t,
                        notes: t.notes.map(n => 
                            n._id === tempNoteId ? { ...n, uploadProgress: percent } : n
                        )
                    };
                }
                return t;
            }));
        });

        if (result.success && result.data) {
            setTickets(prev => prev.map(t => t._id === ticketId ? result.data : t));
        } else {
            showAlert(`Failed to add note: ${result.error}`, 'Error');
            setTickets(prev => prev.map(t => {
                if (t._id === ticketId) {
                    return { ...t, notes: t.notes.filter(n => n._id !== tempNoteId) };
                }
                return t;
            }));
        }

        return result;
    }, [apiCallWithFile, user, showAlert]);

    const handleUpdateNote = async (ticketId, noteId, note) => {
        const result = await apiCall(`tickets/${ticketId}/notes/${noteId}`, 'PUT', note);
        if(result.success) setTickets(prev => prev.map(t => t._id === ticketId ? result.data : t));
        return result;
    }
 
    const handleDeleteNote = async (ticketId, noteId) => {
        const result = await apiCall(`tickets/${ticketId}/notes/${noteId}`, 'DELETE');
        if(result.success) setTickets(prev => prev.map(t => t._id === ticketId ? result.data : t));
        return result;
    }

    const handleRequestAssignment = async (ticketId) => {
        const result = await apiCall(`tickets/${ticketId}/request-assignment`, 'POST');
        if (result.success && result.data) {
            setTickets(prev => prev.map(t => t._id === ticketId ? result.data : t));
        }
        return result;
    };

    const handleCheckIn = async (ticketId) => {
        const result = await apiCall(`tickets/${ticketId}/checkin`, 'POST');
        if (result.success && result.data) {
            setTickets(prev => prev.map(t => t._id === ticketId ? result.data : t));
        }
        return result;
    };

    const removeUploadTask = useCallback((id) => {
        setBackgroundUploads(prev => {
            const newState = { ...prev };
            delete newState[id];
            return newState;
        });
    }, []);

    const handleCheckOut = useCallback((ticketId, checkoutData) => {
        const { resolutionNote, newStatus, files, inventoryUsed } = checkoutData;
        const tempNoteId = `temp-${Date.now()}`;
        const hasAttachments = files.length > 0;
        
        const optimisticAttachments = files.map(file => ({
            _id: `temp-file-${Math.random()}`,
            originalName: file.name,
            fileType: file.type,
            filePath: URL.createObjectURL(file), 
            isOptimistic: true
        }));

        let inventoryText = '';
        if (inventoryUsed && inventoryUsed.length > 0) {
            inventoryText = `Inventory Used:\n${inventoryUsed.map(i => `- ${i.quantity}x ${i.name}`).join('\n')}\n\n`;
        }

        const fullNoteText = `${inventoryText}Checking out...\nResolution Note: ${resolutionNote}`;

        const optimisticNote = {
            _id: tempNoteId,
            text: fullNoteText,
            author: user?.name || 'You',
            authorId: user?.id,
            createdAt: new Date().toISOString(),
            attachments: optimisticAttachments,
            isUploading: hasAttachments,
            uploadProgress: 0
        };

        setTickets(prev => prev.map(t => {
            if (t._id === ticketId) {
                return {
                    ...t,
                    status: newStatus,
                    notes: [optimisticNote, ...t.notes]
                };
            }
            return t;
        }));

        apiCall(`tickets/${ticketId}/checkout`, 'POST', {
            resolutionNote,
            newStatus,
            hasPendingUploads: hasAttachments,
            inventoryUsed 
        }).then(result => {
            if (result.success && result.data && result.data.ticket) {
                const updatedTicket = result.data.ticket;
                const realNoteId = result.data.noteId;

                setTickets(prev => prev.map(t => {
                    if (t._id === ticketId) {
                        return {
                            ...updatedTicket,
                            notes: updatedTicket.notes.map(n => {
                                if (n._id === realNoteId) {
                                    return { ...n, uploadProgress: 0, isUploading: hasAttachments };
                                }
                                return n;
                            })
                        };
                    }
                    return t;
                }));

                if (hasAttachments && realNoteId) {
                    const formData = new FormData();
                    files.forEach(file => formData.append('attachments', file));
                    apiCallWithFile(`tickets/${ticketId}/notes/${realNoteId}/attach`, formData, (percent) => {
                        setTickets(prev => prev.map(t => {
                            if (t._id === ticketId) {
                                return {
                                    ...t,
                                    notes: t.notes.map(n => 
                                        n._id === realNoteId ? { ...n, uploadProgress: percent, isUploading: true } : n
                                    )
                                };
                            }
                            return t;
                        }));
                    }).then(uploadResult => {
                        if (uploadResult.success && uploadResult.data) {
                            setTickets(prev => prev.map(t => t._id === ticketId ? uploadResult.data : t));
                        } else {
                            showAlert(`File upload failed: ${uploadResult.error}`, 'Error');
                            setTickets(prev => prev.map(t => {
                                if (t._id === ticketId) {
                                    return {
                                        ...t,
                                        notes: t.notes.map(n => 
                                            n._id === realNoteId 
                                                ? { ...n, isUploading: false, text: n.text + '\n[Upload Failed]' } 
                                                : n
                                        )
                                    };
                                }
                                return t;
                            }));
                        }
                    });
                }
            } else {
                showAlert(`Checkout failed: ${result.error}`, 'Error');
                refreshData(); 
            }
        });

        return Promise.resolve({ success: true });
    }, [apiCall, apiCallWithFile, user, showAlert, refreshData]);

    const handleAddInventoryToTicket = useCallback(async (ticketId, itemData) => {
        const result = await apiCall(`tickets/${ticketId}/inventory`, 'POST', itemData);
        if (result.success) {
            setTickets(prev => prev.map(t => t._id === ticketId ? result.data : t));
            refreshData(); 
        } else {
            showAlert(result.error, "Inventory Error");
        }
        return result;
    }, [apiCall, refreshData, showAlert]);

    const handleLogout = () => { sessionStorage.clear(); logout(); };
    const handleTicketDashboardChange = (change) => { updateSettings({ ticketDashboard: { ...(settings.ticketDashboard || { lists: [], layouts: {} }), ...change } }); };
    const handleTechnicianDashboardChange = (change) => { updateSettings({ technicianDashboard: { ...(settings.technicianDashboard || { lists: [], layouts: {} }), ...change } }); };

    const showLoadingScreen = isLoading || (isAuthenticated && isSettingsLoading);

    const mainRoutes = (
        <Routes>
            <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/" />} />
            <Route element={<ProtectedRoute />}>
                <Route path="/" element={<Dashboard tickets={tickets} communities={communities} sites={sites} inventory={inventory} technicians={technicians} appointments={appointments} />} />
                <Route path="/customers" element={<Customers communities={communities} onAddCommunity={handleAddCommunity} onDeleteCommunity={handleDeleteCommunity} onDeleteMultipleCommunities={handleDeleteMultipleCommunities} />} />
                <Route path="/customers/:communityId" element={<CommunityDetails communities={communities} tickets={tickets} contacts={contacts} credentials={credentials} documents={documents} onUpdateCommunity={handleUpdateCommunity} onAddContact={handleAddContact} onUpdateContact={handleUpdateContact} onDeleteContact={handleDeleteContact} onAddCredential={handleAddCredential} onUpdateCredential={handleUpdateCredential} onDeleteCredential={handleDeleteCredential} onAddDocument={handleAddDocument} onDeleteDocument={handleDeleteDocument} apiCallForBlob={apiCallForBlob} />} />
                
                <Route path="/tickets" element={
                    <Tickets 
                        isLoading={showLoadingScreen}
                        tickets={tickets} 
                        technicians={technicians} 
                        communities={communities} 
                        contacts={contacts} 
                        departments={departments} 
                        onCreateTicket={handleCreateTicket} 
                        onDeleteMultipleTickets={handleDeleteMultipleTickets} 
                        userDashboardSettings={settings.ticketDashboard} 
                        onDashboardChange={handleTicketDashboardChange} 
                        appointments={appointments} 
                        ticketGroups={ticketGroups} 
                        onAddGroup={handleAddGroup} 
                        onDeleteGroup={handleDeleteGroup}
                        onUpdateTicket={handleUpdateTicket} 
                        onAddContact={handleAddContact}
                    />} 
                />

                <Route path="/tickets/:ticketId" element={
                    <TicketDetails 
                        tickets={tickets} 
                        technicians={technicians} 
                        communities={communities} 
                        contacts={contacts} 
                        inventory={inventory} 
                        appointments={appointments} 
                        departments={departments} 
                        onDeleteTicket={handleDeleteTicket} 
                        onUpdateTicket={handleUpdateTicket} 
                        onAddNote={handleAddNote} 
                        onUpdateNote={handleUpdateNote} 
                        onDeleteNote={handleDeleteNote} 
                        onAddInventoryToTicket={handleAddInventoryToTicket} 
                        onRemoveInventoryFromTicket={apiCall} 
                        onDeleteAppointment={handleDeleteAppointment} 
                        ticketGroups={ticketGroups} 
                        documents={documents} 
                        onAddDocument={handleAddDocument} 
                        onDeleteDocument={handleDeleteDocument} 
                        apiCallForBlob={apiCallForBlob} 
                        onCheckIn={handleCheckIn} 
                        onCheckOut={handleCheckOut} 
                        onRequestAssignment={handleRequestAssignment} 
                        onCreateSubTicket={handleCreateSubTicket} 
                        onAddContact={handleAddContact}
                        sites={sites}
                    />
                } />
                
                <Route path="/technicians" element={
                    <ProtectedRoute>
                        <Technicians 
                            technicians={technicians} 
                            onAddTechnician={handleAddTechnician} 
                            onUpdateTechnician={handleUpdateTechnician} 
                            onDeleteMultipleTechnicians={handleDeleteMultipleTechnicians} 
                            specializations={specializations} 
                            onAddSpecialization={handleAddSpecialization} 
                            onUpdateSpecialization={handleUpdateSpecialization} 
                            onDeleteSpecialization={handleDeleteSpecialization} 
                            userDashboardSettings={settings.technicianDashboard} 
                            onDashboardChange={handleTechnicianDashboardChange} 
                            users={users} 
                            onlineUserIds={onlineUserIds}
                        />
                    </ProtectedRoute>
                } />
                
                <Route path="/technicians/:technicianId" element={<ProtectedRoute><TechnicianDetails tickets={tickets} technicians={technicians} appointments={appointments} onUpdateTechnician={handleUpdateTechnician} specializations={specializations} /></ProtectedRoute>} />
                
                <Route 
                    path="/schedule" 
                    element={<Schedule 
                        appointments={appointments} 
                        onUpdateAppointment={handleUpdateAppointment} 
                        onDeleteAppointment={handleDeleteAppointment}
                        tickets={tickets}
                        technicians={technicians}
                        communities={communities}
                    />} 
                />

                <Route path="/schedule/new/:ticketId" element={<ProtectedRoute roles={['Giga Admin', 'Admin']}><CreateAppointment tickets={tickets} communities={communities} technicians={technicians} onAddAppointment={handleAddAppointment} /></ProtectedRoute>} />
                
                <Route path="/inventory" element={
                    <Inventory 
                        sites={sites} 
                        communities={communities} 
                        inventory={inventory} 
                        technicians={technicians} 
                        masterInventoryItems={masterInventoryItems} 
                        onAddSite={handleAddSite} 
                        onDeleteMultipleSites={handleDeleteMultipleSites} 
                        onAddItem={handleAddItem} 
                        onAdjustStock={handleAdjustStock} 
                        onDeleteMultipleInventoryItems={handleDeleteMultipleInventoryItems} 
                        onTransferInventory={handleTransferInventory}
                        onConsumeItem={handleConsumeItem}
                    />
                } />

                <Route path="/master-inventory" element={
                    <ProtectedRoute roles={['Giga Admin', 'Admin']}>
                        <MasterInventory 
                            masterInventoryItems={masterInventoryItems} 
                            onAddMasterItem={handleAddMasterItemWithFile} 
                            onUpdateMasterItem={handleUpdateMasterItemWithFile} 
                            onDeleteMultipleMasterItems={deleteMultipleWrapper('master-inventory', setMasterInventoryItems)} 
                        />
                    </ProtectedRoute>
                } />

                <Route path="/inventory/checkout/:masterItemId" element={
                    <ProtectedRoute>
                        <QuickItemCheckout sites={sites} />
                    </ProtectedRoute>
                } />

                <Route path="/inventory/checkout-unit/:unitId" element={
                    <ProtectedRoute>
                        <QuickItemCheckout sites={sites} />
                    </ProtectedRoute>
                } />

                <Route path="/reports" element={<ProtectedRoute roles={['Giga Admin', 'Admin']}><Reports /></ProtectedRoute>} />
                <Route path="/settings" element={<Settings apiCall={apiCall} isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} animationsEnabled={animationsEnabled} toggleAnimations={toggleAnimations} departments={departments} onAddDepartment={handleAddDepartment} onUpdateDepartment={handleUpdateDepartment} onDeleteDepartment={handleDeleteDepartment} />} />

                <Route 
                    path="/settings/users" 
                    element={
                        <ProtectedRoute roles={['Giga Admin', 'Admin']}>
                            <UserManagement departments={departments} users={users} setUsers={setUsers}/>
                        </ProtectedRoute>
                    } 
                />
            </Route>
            <Route path="*" element={<Navigate to={isAuthenticated ? "/" : "/login"} />} />
        </Routes>
    );

    if (!isAuthenticated) return <Routes><Route path="*" element={<Login />} /></Routes>;

    if (isMobile) {
        return (
            <div className="mobile-app-container">
                <GlobalModal />
                <MobileHeader 
                    isDarkMode={isDarkMode}
                    toggleDarkMode={toggleDarkMode}
                    onLogout={handleLogout}
                    isLoading={showLoadingScreen} 
                />
                <main className="mobile-main">
                    <div className="content-area">
                        {mainRoutes}
                    </div>
                </main>
                <MobileNav />
            </div>
        );
    }
    
    return (
        <div className="app-container">
            <GlobalModal />
            <GlobalUploadProgress uploads={backgroundUploads} onClose={removeUploadTask} />
            <Sidebar />
            <div className="main-content">
                <Header 
                    onLogout={handleLogout} 
                    isDarkMode={isDarkMode} 
                    toggleDarkMode={toggleDarkMode} 
                    notifications={notifications} 
                    markNotificationsAsRead={markNotificationsAsRead} 
                    clearAllNotifications={clearAllNotifications}
                    tickets={tickets}
                    communities={communities}
                    technicians={technicians}
                    inventory={inventory} 
                    sites={sites}
                    isLoading={showLoadingScreen}
                />
                <main className="content-area">{mainRoutes}</main>
            </div>
        </div>
    );
};

const App = () => (
    <BrowserRouter>
        <AppContent />
    </BrowserRouter>
);

export default App;